import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { deepResearchSendIcon } from '../../commonImage';
import { useDispatch, useSelector } from 'react-redux';
import {
  setIsDeepResearchEnabled,
  setIsOpenAIEnabled
} from '../../store/actions/deepResearchActions';
import DeepResearchIcon from '../../../src/assets/images/deep-research/menu-brain-research.svg';
import OpenAIIcon from '../../../src/assets/images/deep-research/globe.svg';
import SelectedMenuIcon from '../../../src/assets/images/deep-research/selected-menu-icon.svg';
import ChipCloseIcon from '../../../src/assets/images/deep-research/chip-close.svg';
import AddMenuIcon from '../../../src/assets/images/deep-research/add-menu.svg';
import PageConfig from '../../ui-constants/en.json';

interface InputProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  placeholder?: string;
  isFromThreadView?: boolean;
}

export const DeepResearchInput: React.FC<InputProps> = ({
  value,
  onChange,
  onSend,
  placeholder = 'Ask anything...',
  isFromThreadView
}) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state: any) => state.userChat.isLoading);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const selectedThreadMessages = useSelector(
    (state: any) => state.deepResearch.selectedThreadMessages
  );
  const [isOpenAISelectionAllowed, setIsOpenAISelectionAllowed] = useState(false);

  useEffect(() => {
    if (selectedThreadMessages?.length > 1) {
      const hasDeepSearchMessage = selectedThreadMessages.some((msg: any) => {
        try {
          return JSON.parse(msg.response)?.type === 'final';
        } catch {
          return false;
        }
      });
      setIsOpenAISelectionAllowed(hasDeepSearchMessage);
    }
  }, [selectedThreadMessages]);

  useEffect(() => {
  const textarea = textareaRef.current;
  if (textarea) {
    // Reset height first to get accurate scrollHeight measurement
    if (!value.trim()) {
      // Empty content: reset to minimum height
      textarea.rows = isFromThreadView ? 1 : 15;
      textarea.style.overflowY = 'hidden';
      return;
    }

    // Temporarily reset height to get accurate scrollHeight
    const originalRows = textarea.rows;
    textarea.rows = 1;
    
    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight) || 15;
    const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const contentHeight = textarea.scrollHeight - padding;
    const approxLines = Math.round(contentHeight / lineHeight);

    if (isFromThreadView) {
      // ThreadView: 1 → 3 → 5 → 10 → scroll
      if (approxLines <= 1) {
        textarea.rows = 1;
        textarea.style.overflowY = 'hidden';
      } else if (approxLines <= 3) {
        textarea.rows = 3;
        textarea.style.overflowY = 'hidden';
      } else if (approxLines <= 5) {
        textarea.rows = 5;
        textarea.style.overflowY = 'hidden';
      } else if (approxLines <= 10) {
        textarea.rows = 10;
        textarea.style.overflowY = 'hidden';
      } else if (approxLines <= 20) {
        textarea.rows = 20;
        textarea.style.overflowY = 'hidden';
      } else {
        textarea.rows = 20;
        textarea.style.overflowY = 'auto';
      }
    } else {
      // Regular view: default 5 → 10 → scroll
      textarea.rows = 15;
      textarea.style.overflowY = 'hidden';

      if (approxLines >= 15) {
        textarea.rows = 15;
        textarea.style.overflowY = 'auto';
      }
    }
  }
}, [value, isFromThreadView]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };
  const [menuOpen, setMenuOpen] = useState(false);
  const [deepResearchSelected, setDeepResearchSelected] = useState(true);
  const [openAISelected, setOpenAISelected] = useState(false);

  useEffect(() => {
    setDeepResearchSelected(true);
    dispatch(setIsDeepResearchEnabled(true));
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      setMenuOpen(false);
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen((open) => !open);
  };

  const icons = {
    deepResearch: <img src={DeepResearchIcon} alt='deep research' />,
    openAI: <img src={OpenAIIcon} alt='open ai' />
  };

  const handleMenuSelect = (isDeepResearchMenu: boolean) => {
    setDeepResearchSelected(isDeepResearchMenu);
    setOpenAISelected(!isDeepResearchMenu);
    dispatch(setIsDeepResearchEnabled(isDeepResearchMenu));
    dispatch(setIsOpenAIEnabled(!isDeepResearchMenu));
    setMenuOpen(false);
  };

  const handleRemoveChip = () => {
    setDeepResearchSelected(false);
    setOpenAISelected(false);
    dispatch(setIsDeepResearchEnabled(false));
    dispatch(setIsOpenAIEnabled(false));
  };

  return (
    <>
      <StyledCard isFromThreadView={isFromThreadView}>
        {/* First div: only textarea */}
        <TextareaContainer>
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            aria-label='Research input'
            disabled={isLoading}
            // maxLength={PageConfig.deepResearchQueryLimit}
          />
        </TextareaContainer>
        {/* Second div: controls row */}
        <ControlsRow>
          <MenuWrapper>
            <MenuTrigger
              aria-label='Show more options'
              onClick={handleMenuClick}
              tabIndex={0}
              type='button'
              disabled={isLoading}>
              <img src={AddMenuIcon} alt='Add menu' />
            </MenuTrigger>
            {menuOpen && (
              <DropdownMenu
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}>
                <MenuItem onClick={() => handleMenuSelect(true)}>
                  <>
                    {icons.deepResearch}{' '}
                    <span className={'menu-option' + (deepResearchSelected ? ' selected' : '')}>
                      Deep Research
                    </span>{' '}
                    <img src={deepResearchSelected ? SelectedMenuIcon : ''} alt='' />
                  </>
                </MenuItem>
                <MenuItem
                  onClick={() => handleMenuSelect(false)}
                  disabled={!isOpenAISelectionAllowed}>
                  <>
                    {icons.openAI}{' '}
                    <span className={'menu-option' + (openAISelected ? ' selected' : '')}>
                      Open AI
                    </span>{' '}
                    <img src={openAISelected ? SelectedMenuIcon : ''} alt='' />
                  </>
                </MenuItem>
                {/* <Divider />
              <MenuItem>{icons.attach} Attach file</MenuItem> */}
              </DropdownMenu>
            )}
          </MenuWrapper>
          <ChipsFlex>
            {deepResearchSelected && (
              <ChipRow>
                <Chip>
                  <ChipIcon>{icons.deepResearch}</ChipIcon>
                  <span className='chip-title'>Deep Research</span>
                  {/* <ChipCancel
                    aria-label='Remove Research'
                    onClick={handleRemoveChip}
                    tabIndex={0}
                    disabled={isLoading}>
                    <img src={ChipCloseIcon} alt='Remove Research' />
                  </ChipCancel> */}
                </Chip>
              </ChipRow>
            )}
            {openAISelected && (
              <ChipRow>
                <Chip>
                  <ChipIcon>{icons.openAI}</ChipIcon>
                  <span className='chip-title'>Open AI</span>
                  {/* <ChipCancel
                    aria-label='Remove Open AI'
                    onClick={handleRemoveChip}
                    tabIndex={0}
                    disabled={isLoading}>
                    <img src={ChipCloseIcon} alt='Remove Open AI' />
                  </ChipCancel> */}
                </Chip>
              </ChipRow>
            )}
          </ChipsFlex>
          <StyledActions>
            <StyledActionBtn
              className={isLoading ? 'block-send' : ''}
              aria-label='Send'
              disabled={!value || isLoading}
              type='button'
              onClick={onSend}>
              <img src={deepResearchSendIcon} alt='Send' />
            </StyledActionBtn>
          </StyledActions>
        </ControlsRow>
      </StyledCard>
      {/* <CharCount
        isFromThreadView={isFromThreadView}
        style={{ visibility: value.length > 0 ? 'visible' : 'hidden' }}>
        ({value.length}/{PageConfig.deepResearchQueryLimit})
      </CharCount> */}
    </>
  );
};

const ChipRow = styled.div`
  display: none;
  align-items: center;
  gap: 8px;
`;

const Chip = styled.div`
  display: inline-flex;
  background: rgb(245, 250, 255);
  padding: 2px 10px 2px 6px;
  color: rgb(18, 117, 193);
  font-weight: 400;
  cursor: default;
  gap: 4px;
  color: #0a558e;
  text-align: center;
  font-family: 'ey_interstate';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  border-radius: 16px;
  border: 1px solid #afcdfb;

  .chip-title {
    color: #0a558e;
    text-align: center;
    font-family: EYInterstate;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
  }
`;

const ChipIcon = styled.span`
  color: #1275c1;
  font-size: 1.1em;
  display: flex;
  align-items: center;
`;

const ChipCancel = styled.button`
  background: none;
  border: none;
  color: #1275c1;
  font-size: 1.1em;
  margin-left: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
  &:hover,
  &:focus {
    color: #0d5fa6;
  }
  &:disabled {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.5;
  }
`;

const MenuWrapper = styled.div`
  position: relative;
  display: none;
  align-items: center;
`;

const MenuTrigger = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  cursor: pointer;
  transition: background 0.2s;
  padding: 0px;

  &:disabled {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.5;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 30px;
  left: 0;
  min-width: 12.5rem;
  background: #fff;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.13);
  padding: 8px 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MenuItem = styled.button`
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  font-size: 14px;
  color: #222;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover,
  &:focus {
    background: #f3f3f5;
  }

  .menu-option {
    flex: 1;
    min-width: 0;
  }

  .selected {
    overflow: hidden;
    color: #1275c1;
    text-overflow: ellipsis;
  }
  &:disabled {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.5;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #e0e0e5;
  margin: 4px 0;
`;

const StyledCard = styled.div<{ isFromThreadView?: boolean }>`
  background: #fff;
  border: 1px solid #d8e5fd;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  padding: 12px;
  display: flex;
  flex-direction: column;
  // max-width: ${(props) => (props.isFromThreadView ? '100%' : '700px')};
  // margin: 0 auto;
  position: relative;
`;

const TextareaContainer = styled.div``;

const InputColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end; /* <-- Ensure chip and textarea are at the bottom */
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 5px;
`;

const Textarea = styled.textarea`
  width: 100%;
  // min-height: 38px;
  // max-height: 200px;
  font-size: 1.1rem;
  font-family: inherit;
  outline: none;
  background: transparent;
  color: #1a1a24;
  line-height: 1.5;
  border: none;
  resize: none;
  flex: 1 1 auto;
  font-family: 'ey_interstate';
  font-weight: 300;
`;

const StyledActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1.2rem;
  margin-bottom: 0.2rem;
`;

const StyledActionBtn = styled.button`
  height: 32px;
  width: 32px;
  background: #2e2e38;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  cursor: pointer;
  transition: background 0.2s;
  padding: 0px;
  border-radius: 5px;

  &:disabled {
    background: #9897a6;
    cursor: not-allowed;
    pointer-events: none;
    opacity: 1;
  }

  .block-send {
    cursor: not-allowed;
  }
`;

const ChipsFlex = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow-x: auto;
`;

const CharCount = styled.div<{ isFromThreadView?: boolean }>`
  font-size: 13px;
  color: #9897a6;
  text-align: right;
  margin: 5px auto;
  max-width: ${(props) => (props.isFromThreadView ? '100%' : '700px')};
`;

export default DeepResearchInput;
